import { MAX_BASE, SCORING } from './config';
import { haversineKm } from './geo';
import type { Guess, LatLng, QuestionScore } from './types';

/** Where points = round(70 × e^(−d / 2000)), full marks within 10 km (PRD §4.3). */
export function wherePoints(distanceKm: number): number {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) {
    throw new RangeError(`distanceKm must be a non-negative number, got ${distanceKm}`);
  }
  if (distanceKm <= SCORING.fullMarksRadiusKm) return SCORING.wherePoints;
  return Math.round(SCORING.wherePoints * Math.exp(-distanceKm / SCORING.distanceDecayKm));
}

/** When points = max(0, 30 − 3 × |years off|) (PRD §4.3). */
export function whenPoints(yearDiff: number): number {
  if (!Number.isFinite(yearDiff)) throw new RangeError(`yearDiff must be finite, got ${yearDiff}`);
  const diff = Math.abs(Math.trunc(yearDiff));
  return Math.max(0, SCORING.whenPoints - SCORING.pointsPerYear * diff);
}

/** Multiplier for a 1-based question position: Q1 ×1, Q2 ×1, Q3 ×2, Q4 ×3, Q5 ×3. */
export function multiplierFor(position: number): number {
  const m = Number.isInteger(position) ? SCORING.multipliers[position - 1] : undefined;
  if (m === undefined) throw new RangeError(`Invalid question position ${position}`);
  return m;
}

export interface ScoreInput {
  position: number;
  guess: Guess;
  answer: LatLng & { year: number };
}

/** Score one question. Pure; used by both the API and the web app. */
export function scoreQuestion({ position, guess, answer }: ScoreInput): QuestionScore {
  const multiplier = multiplierFor(position);
  const distanceKm = haversineKm(guess, answer);
  const yearDiff = guess.year - answer.year;
  const wherePts = wherePoints(distanceKm);
  const whenPts = whenPoints(yearDiff);
  const basePts = wherePts + whenPts;
  return {
    distanceKm,
    yearDiff,
    wherePts,
    whenPts,
    basePts,
    multiplier,
    questionPts: basePts * multiplier,
  };
}

/** Sum of multiplied question points. */
export function totalOf(scores: ReadonlyArray<Pick<QuestionScore, 'questionPts'>>): number {
  return scores.reduce((sum, s) => sum + s.questionPts, 0);
}

/** True if a base score is the best possible for a question. */
export const isPerfect = (basePts: number): boolean => basePts >= MAX_BASE;
